import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { RefreshCw, Gift, Loader2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token') || sessionStorage.getItem('access_token')}`,
});

const fmtN = (v) => Number(v || 0).toLocaleString('pt-BR');
const fmtDate = (s) => (s ? new Date(s).toLocaleString('pt-BR') : '-');

const STATUS_LABEL = {
  PENDING: { txt: 'Na fila', cls: 'bg-yellow-100 text-yellow-800' },
  PROCESSING: { txt: 'Creditando', cls: 'bg-blue-100 text-blue-800' },
  FAILED: { txt: 'Tentando de novo', cls: 'bg-orange-100 text-orange-800' },
  MANUAL_REVIEW: { txt: 'Parado — precisa de você', cls: 'bg-red-100 text-red-800' },
};

export default function RoulettePrizeFulfillment() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/event-campaigns/roulette/prize-fulfillment`,
        { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      toast.error('Erro ao carregar os prêmios da roleta');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const retry = async (spinId) => {
    setBusy(spinId || 'all');
    try {
      const r = await axios.post(`${API}/event-campaigns/roulette/prize-fulfillment/retry`,
        { spin_id: spinId || null }, { headers: authHeaders() });
      toast.success(`${r.data.requeued} prêmio(s) na fila · ${r.data.delivered} entregue(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Não foi possível reprocessar');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <Card data-testid="roulette-prizes-card">
        <CardContent className="py-6 text-sm text-gray-600">Carregando prêmios...</CardContent>
      </Card>
    );
  }

  const travados = data?.stuck_total || 0;

  return (
    <Card
      data-testid="roulette-prizes-card"
      className={travados ? 'border-red-400 bg-red-50/60' : 'border-emerald-300 bg-emerald-50/40'}
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Entrega dos prêmios
            {travados > 0 && (
              <Badge className="bg-red-600 text-white" data-testid="roulette-prizes-badge">
                {travados}
              </Badge>
            )}
          </span>
          <span className="flex items-center gap-2">
            {travados > 0 && (
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="roulette-prizes-retry-all"
                disabled={busy !== null}
                onClick={() => retry(null)}
              >
                {busy === 'all' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reprocessar todos'}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={load} data-testid="roulette-prizes-refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </span>
        </CardTitle>
        <CardDescription>
          Prêmio que a KAKO recusou (saldo zerado, por exemplo) fica na fila e é tentado de novo
          automaticamente. Saldo KAKO agora:{' '}
          <b data-testid="roulette-prizes-kako-balance">{fmtN(data?.kako_balance)}</b> moedas.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
          <div className="p-2 rounded-lg bg-white border" data-testid="roulette-prizes-stuck">
            <p className="text-xs text-gray-500">Sem chegar na conta</p>
            <p className={`text-lg font-bold ${travados ? 'text-red-600' : 'text-emerald-600'}`}>
              {travados}
            </p>
          </div>
          <div className="p-2 rounded-lg bg-white border">
            <p className="text-xs text-gray-500">Moedas paradas</p>
            <p className="text-lg font-bold text-gray-900">{fmtN(data?.stuck_coins)}</p>
          </div>
          <div className="p-2 rounded-lg bg-white border" data-testid="roulette-prizes-review">
            <p className="text-xs text-gray-500">Parados de vez</p>
            <p className={`text-lg font-bold ${data?.manual_review ? 'text-red-600' : 'text-emerald-600'}`}>
              {data?.manual_review || 0}
            </p>
          </div>
          <div className="p-2 rounded-lg bg-white border">
            <p className="text-xs text-gray-500">Entregues</p>
            <p className="text-lg font-bold text-emerald-600">{data?.delivered || 0}</p>
          </div>
        </div>

        {data?.paused && (
          <p className="flex items-center gap-2 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4" /> A entrega automática está PAUSADA nas configurações.
          </p>
        )}

        {travados === 0 ? (
          <p className="text-sm text-emerald-700" data-testid="roulette-prizes-ok">
            ✅ Todo prêmio sorteado já está na conta do ganhador.
          </p>
        ) : (
          <div className="space-y-2" data-testid="roulette-prizes-list">
            {data.items.map((i) => {
              const st = STATUS_LABEL[i.fulfillment_status] || STATUS_LABEL.PENDING;
              return (
                <div
                  key={i.spin_id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2"
                  data-testid={`roulette-prize-item-${i.spin_id.slice(0, 8)}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900">
                      {fmtN(i.yellow_prize)} moedas · KAKO {i.kako_id}
                      {i.nickname ? ` (${i.nickname})` : ''}
                    </p>
                    <p className="text-[11px] text-gray-500">
                      {fmtDate(i.resolved_at)} · tentativas: {i.fulfillment_attempts || 0}
                      {i.fulfillment_error ? ` · ${i.fulfillment_error}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {i.awaiting_balance && (
                      <Badge className="bg-amber-100 text-amber-800">Esperando saldo KAKO</Badge>
                    )}
                    <Badge className={st.cls}>{st.txt}</Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`roulette-prize-retry-${i.spin_id.slice(0, 8)}`}
                      disabled={busy !== null}
                      onClick={() => retry(i.spin_id)}
                    >
                      {busy === i.spin_id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Tentar agora'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
